import { BookOpen, GraduationCap } from "lucide-react";
import { useTranslation } from "react-i18next";
import ImageOrVideo from "./ImageOrVideo";
import { Text } from "@core/index";
import { CourseData } from "@packages/constant/CourseData";
import { tC } from "../../utils/TranslateWithContentLanguage";

interface CardImageProps {
  imageUrl?: string;
  isLoading?: boolean;
  badges?: React.ReactNode;
  courseName?: string;
  gradeLevel?: number;
  topic?: string | React.ReactNode;
  createdAt?: string;
  theme?:
    | "red" // Misconceptions (red-orange)
    | "orange" // Connections (orange-red)
    | "blue" // Hooks & Vocabularies (blue-indigo)
    | "teal" // Summaries (teal-emerald)
    | "cyan" // (existing color)
    | "rose" // Slides (rose)
    | "pink" // Exit Tickets
    | "lime" // Homework Ideas
    | "terra" // Transfer activity
    | "purple" // Performance Tasks
    | "indigo" // Project Based Learning
    | "amber" // Rubric
    | "emerald" // Collaborative
    | "violet" // Classroom Management
    | "sky" // Essential Questions
    | "yellow" // Formative Assessment
    | "fuchsia" // Transfer Activities
    | "green" // Choice Board
    | "stone" // Exit Tickets
    | "coral"
    | "mint"
    | "lavender"
    | "sunset"
    | "ocean"
    | "brown"
    | "bloom"; // Bloom Taxonomy

  videoUrl?: string;
  resolution?: "high" | "medium" | "original";
  contentLanguage?: string;
}
const themes = {
  red: "bg-red-500/90", // Misconceptions
  orange: "bg-orange-500/90", // Connections
  blue: "bg-blue-500/90", // Hooks & Vocabularies
  teal: "bg-teal-500/90", // Summaries
  cyan: "bg-cyan-500/90", // Existing color
  rose: "bg-rose-500/90", // Slides
  pink: "bg-pink-500/90", // Exit Tickets
  lime: "bg-lime-500/90", // Homework Ideas
  terra: "bg-[#D98787]/90", // Transfer activity
  purple: "bg-purple-500/90", // Performance Tasks
  indigo: "bg-indigo-500/90", // Project Based Learning
  amber: "bg-amber-500/90", // Rubric
  emerald: "bg-emerald-500/90", // Collaborative
  violet: "bg-violet-500/90", // Classroom Management
  sky: "bg-sky-500/90", // Essential Questions
  yellow: "bg-yellow-500/90", // Formative Assessment
  fuchsia: "bg-fuchsia-500/90", // Transfer Activities
  green: "bg-green-500/90", // Choice Board
  stone: "bg-stone-500/90", // Exit Tickets
  coral: "bg-red-400/90",
  mint: "bg-green-400/90",
  lavender: "bg-purple-400/90",
  sunset: "bg-orange-400/90",
  ocean: "bg-blue-400/90",
  brown: "bg-[#C8B08D]", // Collaborative
  bloom: "bg-[#81C784]",
};

const CardImage = ({
  imageUrl,
  videoUrl,
  isLoading = false,
  badges,
  courseName,
  gradeLevel,
  topic,
  theme,
  resolution = "medium",
  createdAt,
  contentLanguage,
}: CardImageProps) => {
  const { t } = useTranslation();

  const gradientClasses = `absolute inset-0 bg-gradient-to-br from-${theme}-400/60 via-purple-500/40 to-${theme}-600/60 bg-blend-soft-light backdrop-blur-[2px] rounded-xl flex items-center justify-center p-4 shadow-lg shadow-${theme}-500/10`;

  return (
    <div className="space-y-4 md:w-[80%] w-full mx-auto">
      <div className="relative aspect-[16/9] rounded-2xl shadow-lg flex items-center justify-center bg-gray-100 dark:bg-gray-800">
        {!imageUrl ? (
          <div className={gradientClasses}>
            <span className="text-white font-bold text-xl text-center drop-shadow-lg">
              {topic || "Loading..."}
            </span>
          </div>
        ) : (
          <ImageOrVideo
            imageUrl={imageUrl}
            videoUrl={videoUrl}
            isLoading={isLoading}
            toolColor={theme}
            topic={t(`courseType.${courseName}`)}
            resolution={resolution}
            createdAt={createdAt}
          />
        )}
      </div>

      {topic && typeof topic === "string" ? (
        <Text variant={"h1"} className="text-center">
          {topic}
        </Text>
      ) : (
        topic
      )}

      <div className="flex flex-wrap items-center justify-center gap-2">
        {courseName && (
          <div
            className={`flex items-start ${themes[theme as keyof typeof themes]} px-3 py-1.5 rounded-full max-w-full`}
          >
            <BookOpen size={14} className="text-white shrink-0 mt-1" />
            <Text
              variant="span"
              size={"sm"}
              weight={"medium"}
              className="text-white ml-2 break-words"
            >
              {`courseType.${CourseData[
                courseName as keyof typeof CourseData
              ]?.name(gradeLevel ?? 8)}`}
            </Text>
          </div>
        )}
        {gradeLevel !== null &&
          gradeLevel !== undefined &&
          gradeLevel !== 0 && (
            <div
              className={`flex items-start bg-purple-500/90 px-3 py-1.5 rounded-full max-w-full`}
            >
              <GraduationCap size={14} className="text-white shrink-0 mt-1" />
              <Text
                variant="span"
                size={"sm"}
                weight={"medium"}
                className="text-white ml-2 break-words"
              >
                {tC(contentLanguage ?? "tr", "hooks.form.gradeOf", {
                  grade: gradeLevel,
                })}
              </Text>
            </div>
          )}
        {badges}
      </div>
    </div>
  );
};

export default CardImage;
